import React from "react";
import "../styles/ProfileFields.css";

/**
 * A label-and-value list where the two cannot drift apart.
 *
 * The profile panels used to render labels in one `<div>` and values in
 * another, side by side, relying on both lists happening to produce the same
 * sequence of line heights. They did not. Two stray `<br />` tags in the label
 * column, and a label long enough to wrap, were enough to slide every value up
 * against the wrong label — so the admin console showed a patient's date of
 * birth captioned "License Number", and their gender captioned nothing at all.
 *
 * A caption that belongs to a different field is worse than a missing one:
 * nothing looks broken, so it gets believed. On a clinical record that is not
 * a cosmetic bug.
 *
 * Here each label and its value are one row of a grid, written adjacent in the
 * markup. There is no ordering to keep in step, so no amount of wrapping,
 * spacing or empty values can misalign them.
 *
 * @param fields [{ label, value }] — rendered in order. A null, undefined or
 *               blank value shows "Not recorded" rather than an empty gap,
 *               because a blank row reads as a rendering fault.
 */
const ProfileFields = ({ fields }) => (
  <dl className="profile-fields">
    {fields.map(({ label, value }) => {
      const missing =
        value === null || value === undefined || String(value).trim() === "";
      return (
        <React.Fragment key={label}>
          <dt className="profile-fields-label">{label}</dt>
          <dd
            className={
              missing
                ? "profile-fields-value profile-fields-value-missing"
                : "profile-fields-value"
            }
          >
            {missing ? "Not recorded" : value}
          </dd>
        </React.Fragment>
      );
    })}
  </dl>
);

export default ProfileFields;
